import { useEffect, useRef, useState } from "react";
import type {
  FieldSpec,
  FieldValue,
  FormLookup,
  FormSchema,
  FormValues,
  GroupFieldSpec,
  OpenHoldItem,
  ScalarFieldSpec,
  SubmittedFollowOn,
  Theme,
  ValidateResponse,
} from "../types.js";
import { ADDRESS_LOOKUP_RESERVED_LOOKUP_ID } from "../types.js";
import { followOnBudgetMs, pollFollowOn, type FollowOnSettled, type FormBackend } from "../api/formBackend.js";
import type { LookupResult } from "../api/formApi.js";
import { DRAFT_DEBOUNCE_MS, createAutosave, type Autosave, type SaveStatus } from "../api/draftAutosave.js";
import { PageFooter, TenantHeader } from "../components/TenantHeader.js";
import { FormSkeleton } from "../components/FormSkeleton.js";
import { FormRenderer } from "../components/FormRenderer.js";
import { AutofillNotice } from "../components/AutofillNotice.js";
import { LookupAction, LookupResultCard, type LookupStatus } from "../components/LookupAction.js";
import { LookupChoiceList } from "../components/LookupChoiceList.js";
import { ReviewStep, type ReviewCard } from "../components/ReviewStep.js";
import { SaveLater, SaveStatusLine } from "../components/SaveProgress.js";
import { StepNav, StepProgress } from "../components/StepNav.js";
import { StatusCard, type StatusVariant } from "../components/StatusCard.js";
import { Copy, InfoCards } from "../components/ContentBlocks.js";
import { FromCtxPanel } from "../components/FromCtxPanel.js";
import { BlockerPanel } from "../components/BlockerPanel.js";
import {
  ANSWER_YES,
  NOT_ANSWERED,
  answerSummary,
  asRows,
  asString,
  buildPayload,
  changeReasonName,
  changedFromPreAnswer,
  declaredFields,
  defaultsPatch,
  fieldLabel,
  firstIncompleteStep,
  firstStepWithError,
  isGroupField,
  isWizard,
  optionEntries,
  ownsChangeReason,
  preAnswer,
  preAnswerPatch,
  readOnlyShown,
  restoreValues,
  validateFields,
  validateForm,
  visibleFields,
  visibleSteps,
} from "../schema/formModel.js";
import type { SummaryStep } from "../schema/formModel.js";
import { cellLine, envelopeDisplayValue, formatAddress, progressSections, rowSummary, rowTitle, sectionIndexOf } from "../schema/formDesign.js";
import {
  LOOKUP_MESSAGE_UNAVAILABLE,
  chooseCandidates,
  choosePatch,
  declaredLookups,
  lookupArgs,
  lookupGroupProvenance,
  lookupMessage,
  lookupPatch,
  lookupReady,
  readPath,
  type LookupCandidate,
} from "../schema/lookupModel.js";
import { rowScope, type PredicateScope } from "../schema/predicates.js";
import { applyBrandTokens, deriveBrandTokens } from "../theme/brandTokens.js";
import "../components/styles.css";

type ViewState =
  | { kind: "loading" }
  | { kind: "form"; response: ValidateResponse }
  | { kind: "submitted"; response: ValidateResponse }
  /**
   * Phase 572 wave 3 task 3.1 — the gate is not open yet: the form is bound to an approval gate,
   * but the job's current state does not yet wait on that gate (the deferred-walk race). The page
   * shows the server's plain-English message and polls /context until the gate opens.
   */
  | { kind: "waiting"; response: ValidateResponse; message: string }
  /**
   * Phase 572 wave 3 task 3.1 — the polling ceiling was reached and the gate never opened. Terminal
   * state: a plain-English message that the file is not with the approver and a retry action.
   */
  | { kind: "gave-up"; response: ValidateResponse }
  /**
   * BB walk 2 (item 5) — `theme` is the tenant branding a terminal page (already submitted,
   * expired) still wears when the surface knows it; absent ⇒ the platform neutral.
   */
  | { kind: "error"; variant: StatusVariant; theme?: Theme };

/**
 * KYC-design — the practice's brand is the page: derive the full token set from the two
 * colours the tenant supplied and set it on <html>, alongside the logo and favicon.
 */
function applyTheme(theme: ValidateResponse["theme"]) {
  const root = document.documentElement;
  const tokens = deriveBrandTokens({
    primary: theme.primary,
    secondary: theme.secondary,
    accent: theme.accent,
    surface_page: theme.surface_page,
    surface_card: theme.surface_card,
    text_primary: theme.text_primary,
    text_secondary: theme.text_secondary,
  });
  if (tokens) applyBrandTokens(root, tokens);
  root.style.setProperty("--tenant-logo-url", `url(${theme.logo_url})`);
  root.style.setProperty("--tenant-favicon-url", `url(${theme.favicon_url})`);

  let icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (icon === null) {
    icon = document.createElement("link");
    icon.setAttribute("rel", "icon");
    document.head.appendChild(icon);
  }
  icon.setAttribute("href", theme.favicon_url);
}

function setDocumentTitle(title: string) { if (typeof document !== "undefined") document.title = title; }

const EMPTY_SCHEMA: FormSchema = {};
export const DEFAULT_SUBMIT_LABEL = "Submit form";

/**
 * Phase 572 wave 3 task 3.1 — gate-open polling parameters. The wait is CEILINGED: the gate can
 * legitimately never open (the walk routes elsewhere, an on_entry fails, the poller is down), so
 * after a named attempt/wall-clock ceiling the page reaches a terminal give-up state rather than
 * spinning forever.
 */
export const GATE_POLL_INTERVAL_MS = 2000;
export const GATE_POLL_MAX_ATTEMPTS = 30; // 30 attempts * 2s = 60s ceiling

/**
 * Phase 572 wave 3 task 3.1 — the terminal message when the gate never opened. Plain English,
 * naming no gate/state/workflow — the approver must know they cannot act, not why the system
 * cannot serve them.
 */
export const GATE_GAVE_UP_MESSAGE =
  "This file is not currently waiting on your approval. It may have been routed elsewhere, or " +
  "something interrupted its journey. Check with the team handling this file.";

/**
 * What the page says when a submit failed and the engine said nothing about why — a network
 * fault, a 5xx, an unreadable body. Unchanged copy: it is the RETRYABLE case, and "try again" is
 * exactly right for it.
 *
 * <p>A refusal is the other case and gets the engine's own sentence instead (`formBackend.ts`
 * `FormSubmitOutcome`): since Phase 532 this wizard is also an approval door, and the reasons a
 * decision is refused — the wrong role for a band change, a change with no written reason — are
 * sentences only the server can write. Rendering them verbatim is what stops an approver retrying
 * something that will never succeed.</p>
 */
export const SUBMIT_FAILED_MESSAGE =
  "Something went wrong submitting your form. Please check your connection and try again.";

/**
 * Phase 541 — does anything the step is about to render actually declare an address lookup?
 *
 * <p>Group SUBFIELDS count, and that is the whole point of asking here rather than at the field:
 * a real declaration puts most of its address blocks inside repeating groups, which is a reach no
 * declared `lookups:` entry has. The same two-level read the server does when it decides whether
 * the reserved `lookup_id` resolves at all (`FormsApi.FieldListDeclaresAddressLookup`) and the
 * validator does when it demands the integration in the palette (ARD-DSL-036).</p>
 *
 * <p>It gates whether the transport is handed down AT ALL, so a step with no address lookup passes
 * nothing and every control below renders exactly what it rendered before this phase.</p>
 */
function stepDeclaresAddressLookup(fields: readonly FieldSpec[] | undefined): boolean {
  return (fields ?? []).some((field) =>
    isGroupField(field)
      ? (field.fields ?? []).some((sub) => (sub as ScalarFieldSpec).lookup !== undefined)
      : (field as ScalarFieldSpec).lookup !== undefined,
  );
}

interface LookupUiState {
  busy: boolean;
  status: LookupStatus | null;
  /** The envelope of the last successful run — what the result card shows. */
  payload?: unknown;
  /**
   * Phase 524 — the candidates of the last successful run of a `choose` lookup. Present (even
   * empty) means a search has RUN, which is what tells the empty state apart from "not yet asked".
   */
  candidates?: readonly LookupCandidate[];
  /** The candidate the subject picked, by index into {@link candidates}. */
  chosen?: number;
}

const IDLE_LOOKUP: LookupUiState = { busy: false, status: null };

/** What a surface with no follow-on transport shows: the declared fallback, from the first paint. */
const FOLLOW_ON_NO_TRANSPORT: FollowOnSettled = { status: "unavailable" };

/**
 * Phase 569 — the thank-you screen's follow-on action, rendered into {@link StatusCard}'s action
 * slot. EVERY word is the declaration's: the declared `label` as the link text the moment a URL is
 * served, the declared `pending` copy while the poll runs (`settled === null`), the declared
 * `fallback` copy once it ends without one. The served URL is only ever an `href` — never a label,
 * and the declared `source` path is never rendered at all (L-97 rule (d)).
 *
 * <p>`rel="noreferrer"`: the magic-link page's own URL carries the form token, and the link leaves
 * for another host — the Referer header must not take the token with it.</p>
 */
function FollowOnAction({ block, settled }: { block: SubmittedFollowOn; settled: FollowOnSettled | null }) {
  if (settled === null) return <p className="status-card-action-note">{block.pending}</p>;
  if (settled.status === "ready") {
    return (
      <a className="button-primary status-card-action-link" href={settled.url} rel="noreferrer">
        {block.label}
      </a>
    );
  }
  return <p className="status-card-action-note">{block.fallback}</p>;
}

export interface FormExperienceProps {
  backend: FormBackend;
}

export function FormExperience({ backend }: FormExperienceProps) {
  const [view, setView] = useState<ViewState>({ kind: "loading" });
  const [values, setValues] = useState<FormValues>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [stepIndex, setStepIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  // null ⇒ nothing failed. A string ⇒ the sentence to show: the engine's own where it gave one,
  // otherwise {@link SUBMIT_FAILED_MESSAGE}. One piece of state, because there is one band.
  const [submitFailure, setSubmitFailure] = useState<string | null>(null);
  const [lookupUi, setLookupUi] = useState<Record<string, LookupUiState>>({});
  const [autofilled, setAutofilled] = useState<Record<string, string[]>>({});
  const [autofilledRows, setAutofilledRows] = useState<Record<string, number[]>>({});
  const [saveStatus, setSaveStatus] = useState<SaveStatus | null>(null);
  const [savedLater, setSavedLater] = useState(false);
  /**
   * Phase 572 wave 3 task 3.3 — the open blockers on an approval gate. Present only when the form
   * is bound to a gate that is open and has unsettled requires_clear items. Each blocker may be
   * settled with one of two actions: "Received" or "Not required".
   */
  const [blockers, setBlockers] = useState<readonly OpenHoldItem[]>([]);
  // Phase 569 — where the thank-you screen's follow-on poll ended; null ⇒ still polling (or no
  // follow-on declared, in which case it is never read).
  const [followOnSettled, setFollowOnSettled] = useState<FollowOnSettled | null>(null);

  const formHeadingRef = useRef<HTMLHeadingElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);
  const autosaveRef = useRef<Autosave<FormValues> | null>(null);
  const loadedValuesRef = useRef<FormValues | null>(null);
  /**
   * Phase 572 wave 3 task 3.1 — gate-open polling attempt counter. Mutable ref because the poll
   * effect does not need to re-render on every increment, only on the state transition that
   * happens when the ceiling is reached or the gate opens.
   */
  const gatePollAttemptsRef = useRef<number>(0);
  // Phase 561 — the `auto_run` CREDIT LEDGER (see the auto-run effect below). `autoRanRef` holds
  // every lookup id whose once-per-session auto-run credit is spent: by an auto-run as it
  // dispatches, or (card #2664) by the subject's own Search press, recorded as the press is made —
  // a pressed search is this session's run, so it never auto-runs after it, not even on a step
  // re-entry. Concurrent duplicates are `inFlightRef`'s job, not this ledger's; `autoRunVisitRef` is
  // the step visit last observed (by the effect, or by a keystroke on it — see `handleChange`),
  // and `typedThisVisitRef` whether the subject has typed anything during it — once they have, an
  // auto-run can no longer arm on this visit, so a keystroke is never what spends a credit.
  const autoRanRef = useRef<Set<string>>(new Set());
  const autoRunVisitRef = useRef<string | undefined | null>(null);
  const typedThisVisitRef = useRef(false);
  // Card #2664 — the lookups with a call IN FLIGHT right now. `lookupUi[id].busy` drives the
  // control, but it is state: two runs dispatched inside one commit (an auto-run and a press, or
  // two presses) both read the pre-busy closure. This ref is read and written synchronously, so a
  // second identical call can never be sent while the first is still out. It refuses only
  // concurrent duplicates — a press after the first call settled runs as ever.
  const inFlightRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    setSaveStatus(null);
    const autosave = createAutosave<FormValues>(
      (fields) => backend.saveDraft(fields),
      DRAFT_DEBOUNCE_MS,
      setSaveStatus,
    );
    autosaveRef.current = autosave;
    return () => {
      autosave.cancel();
      autosaveRef.current = null;
    };
  }, [backend]);

  useEffect(() => {
    setDocumentTitle("Loading form…");
    backend
      .load()
      .then((outcome) => {
        if (outcome.kind === "signing-in") return;
        if (outcome.kind === "unavailable") {
          if (outcome.theme) applyTheme(outcome.theme);
          setView({ kind: "error", variant: outcome.variant, theme: outcome.theme });
          setDocumentTitle("Form unavailable");
          return;
        }
        const response = outcome.response;
        applyTheme(response.theme);
        const loadedSchema = response.form_schema ?? EMPTY_SCHEMA;
        const restored = restoreValues(loadedSchema, response.draft);
        // Phase 532 — a pre-answer the SERVER resolved from the job's own ctx (ARD-DSL-051)
        // lands here, once, as an ordinary answer. It is applied only where the key is absent,
        // so a draft the assessor already touched always wins; and it is applied BEFORE the
        // resume step is chosen, so a question the file already answered does not read as the
        // first incomplete one.
        const patch = preAnswerPatch(declaredFields(loadedSchema), restored);
        const loadedValues = Object.keys(patch).length > 0 ? { ...restored, ...patch } : restored;
        loadedValuesRef.current = loadedValues;
        setValues(loadedValues);
        setStepIndex(
          response.draft
            ? firstIncompleteStep(loadedSchema, loadedValues, {
                ctx: response.context ?? {},
                fields: loadedValues,
                party: response.party ?? {},
              })
            : 0,
        );
        setLookupUi({});
        // A (re)load is a new form session: the auto-run ledger starts empty with it.
        autoRanRef.current = new Set();
        inFlightRef.current = new Set();
        autoRunVisitRef.current = null;
        typedThisVisitRef.current = false;
        setAutofilled({});
        setAutofilledRows({});
        // Phase 572 wave 3 task 3.1 — the gate-not-open check: if the form is bound to a gate
        // that is not yet open, show the waiting state and start polling. The message is the
        // server's plain-English "this file is not yet ready for this approval".
        if (response.gate_not_open) {
          gatePollAttemptsRef.current = 0;
          setBlockers([]);
          setView({ kind: "waiting", response, message: response.gate_not_open.message });
          const title = response.form_schema?.title ?? "Approval";
          setDocumentTitle(`Waiting — ${title}`);
          return;
        }
        // Phase 572 wave 3 task 3.3 — set the blockers from the response.
        setBlockers(response.blockers ?? []);
        setView({ kind: "form", response });
        const title = response.form_schema?.title ?? "Complete your form";
        setDocumentTitle(`${response.tenant_slug} — ${title}`);
      })
      .catch(() => {
        setView({ kind: "error", variant: "invalid" });
        setDocumentTitle("Form unavailable");
      });
  }, [backend]);

  useEffect(() => {
    if (view.kind !== "form") return;
    if (values === loadedValuesRef.current) return;
    setSavedLater(false);
    autosaveRef.current?.schedule(values);
  }, [values, view.kind]);

  useEffect(() => {
    if (view.kind === "form") {
      setTimeout(() => { firstInputRef.current?.focus(); }, 0);
    }
  }, [view.kind]);

  /**
   * Phase 572 wave 3 task 3.1 — gate-open polling. When the view is "waiting", re-read /context
   * on a bounded poll. If the gate opens (gate_not_open disappears), transition to "form" and
   * render the decision step. If the ceiling is reached, transition to "gave-up".
   */
  useEffect(() => {
    if (view.kind !== "waiting") return;
    const timer = setInterval(() => {
      gatePollAttemptsRef.current += 1;
      if (gatePollAttemptsRef.current >= GATE_POLL_MAX_ATTEMPTS) {
        clearInterval(timer);
        setView({ kind: "gave-up", response: view.response });
        setDocumentTitle("Approval unavailable");
        return;
      }
      // Re-fetch context to check if the gate is now open.
      void backend.load().then((outcome) => {
        if (outcome.kind !== "ready") return;
        const response = outcome.response;
        // Gate is now open — transition to form state.
        if (!response.gate_not_open) {
          clearInterval(timer);
          applyTheme(response.theme);
          const loadedSchema = response.form_schema ?? EMPTY_SCHEMA;
          const restored = restoreValues(loadedSchema, response.draft);
          const patch = preAnswerPatch(declaredFields(loadedSchema), restored);
          const loadedValues = Object.keys(patch).length > 0 ? { ...restored, ...patch } : restored;
          loadedValuesRef.current = loadedValues;
          setValues(loadedValues);
          setStepIndex(0);
          // Phase 572 wave 3 task 3.3 — set the blockers when the gate opens.
          setBlockers(response.blockers ?? []);
          setView({ kind: "form", response });
          const title = response.form_schema?.title ?? "Complete your form";
          setDocumentTitle(`${response.tenant_slug} — ${title}`);
        }
      });
    }, GATE_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [view.kind, backend]);
  // Phase 569 — the follow-on poll, CONFINED to the `submitted` view. It starts on entering that
  // view only when the form DECLARES `submitted.follow_on` (absent ⇒ no call, ever), runs through
  // the seam's one poll loop at the server's `retry_after_ms` for the declared `poll_for`, and is
  // aborted on unmount. A surface whose backend has no follow-on transport settles straight to the
  // declared fallback — never a dead link and never a poll.
  const followOnBlock = view.kind === "submitted" ? view.response.form_schema?.submitted?.follow_on : undefined;
  const followOnTransport = backend.fetchFollowOn;
  useEffect(() => {
    if (followOnBlock === undefined || followOnTransport === undefined) return;
    setFollowOnSettled(null);
    const controller = new AbortController();
    void pollFollowOn(followOnTransport, followOnBudgetMs(followOnBlock.poll_for), controller.signal).then(
      (settled) => { if (settled !== null) setFollowOnSettled(settled); },
    );
    return () => controller.abort();
  }, [followOnBlock, followOnTransport]);

  const schema = view.kind === "form" ? (view.response.form_schema ?? EMPTY_SCHEMA) : EMPTY_SCHEMA;
  const theme = view.kind === "form" || view.kind === "submitted" || view.kind === "waiting" || view.kind === "gave-up"
    ? view.response.theme
    : view.kind === "error"
      ? (view.theme ?? null)
      : null;
  const ctxSnapshot = view.kind === "form" ? (view.response.context ?? {}) : {};
  // Phase 515 — the party snapshot rides the SAME scope object ctx and the answers do, so every
  // predicate and every default already receives it and no call site can forget to pass it. The
  // server's three distinct cases (job-level token / no ledger row / no declared pointer) all
  // flatten to `{}` here: to the renderer they are one case — no party default.
  const partySnapshot = view.kind === "form" ? (view.response.party ?? {}) : {};
  const scope: PredicateScope = { ctx: ctxSnapshot, fields: values, party: partySnapshot };
  const steps = isWizard(schema) ? visibleSteps(schema, scope) : [];
  const wizard = steps.length > 0;
  const currentIndex = wizard ? Math.min(stepIndex, steps.length - 1) : 0;
  const currentStep = steps[currentIndex];
  const isLastStep = !wizard || currentIndex >= steps.length - 1;
  const flatFields = schema.fields ?? [];
  const isEmptySchema = !wizard && flatFields.length === 0;
  const stepLookups = backend.lookup ? (currentStep?.lookups ?? []) : [];
  // Phase 541 — the ONE place an address block reaches the network, owned here and handed down.
  // It is the EXISTING transport (`backend.lookup` → `lookupField` → POST /forms/v0/lookups),
  // invoked with the RESERVED lookup id the server resolves against the job's own declaration and
  // the single `postcode` arg that declaration names. No new backend member and no second
  // never-throws contract: the address arm inherits both from the binding the declared lookups
  // already use. Handed down only where a field actually declares `lookup:`, so every other step
  // renders precisely what it rendered before.
  const lookupTransport = backend.lookup;
  const onAddressLookup =
    lookupTransport !== undefined && stepDeclaresAddressLookup(wizard ? currentStep?.fields : flatFields)
      ? (postcode: string) => lookupTransport(ADDRESS_LOOKUP_RESERVED_LOOKUP_ID, { postcode })
      : undefined;
  const currentStepId = currentStep?.id;
  const currentStepFields = currentStep?.fields;
  const sections = wizard ? progressSections(schema, scope) : [];
  const currentSection = currentStep ? sectionIndexOf(sections, currentStep.id) : -1;
  const isWelcome = wizard && currentIndex === 0 && (currentStep?.fields ?? []).length === 0 && !currentStep?.review;
  const submitLabel = schema.submit_label?.trim() || DEFAULT_SUBMIT_LABEL;
  const reviewStep = (schema.steps ?? []).find((s) => s.review);

  /**
   * The carry-forward defaults (Phase 449), applied on STEP ENTRY for a wizard and ONCE ON LOAD
   * for a flat form — the effect's `[currentStepId, view.kind]` deps fire on the transition to
   * `form`, and a flat form has no step to move between.
   *
   * WHICH LIST is a ternary on `wizard`, not a `??`, and that is load-bearing both ways
   * (Phase 549). A flat form has no `currentStep`, so `currentStepFields` is `undefined`, and an
   * undefined list projects to no visible fields — which is why NO default has ever applied on a
   * flat form, a silent blank box with no error anywhere. And a wizard's CONTENT-ONLY step has an
   * `undefined` field list too, so a `??` would quietly hand that step the whole form's
   * `schema.fields` — a wizard must never see them. Wizard behaviour is byte-identical.
   *
   * What this also gives a flat form is `fields.`/`party.` sources (dormant today — no shipped
   * flat form declares one): applied once on load against the draft-restored answers, so an
   * answered source fills its consumer and an unanswered one is a no-op, exactly as on step entry.
   */
  useEffect(() => {
    if (view.kind !== "form") return;
    setValues((current) => {
      const patch = defaultsPatch(wizard ? currentStepFields : flatFields, current, {
        ctx: ctxSnapshot,
        fields: current,
        party: partySnapshot,
      });
      return Object.keys(patch).length > 0 ? { ...current, ...patch } : current;
    });
  }, [currentStepId, view.kind]);

  /**
   * client walk, 6 Oct 2026 — EVERY STEP STARTS AT THE TOP. Continue, Back, a section jump or the
   * jump to the first step with an error all change the step in place, and the browser kept the
   * old scroll position, so the next step opened halfway down its page. On every step CHANGE
   * (never the first render, which keeps whatever the browser restored) the page scrolls to the
   * top, instantly (no smooth scroll, so nothing animates for a reduced-motion reader), and focus
   * moves to the form's heading so a keyboard or screen-reader user starts the new step from its
   * title rather than from a control that no longer exists. Shared by the client and staff forms.
   */
  const lastStepIdRef = useRef<string | undefined | null>(null);
  useEffect(() => {
    if (view.kind !== "form" || !wizard) return;
    const previous = lastStepIdRef.current;
    lastStepIdRef.current = currentStepId;
    if (previous === null || previous === currentStepId) return;
    if (typeof window !== "undefined" && typeof window.scrollTo === "function") window.scrollTo(0, 0);
    formHeadingRef.current?.focus({ preventScroll: true });
  }, [currentStepId, view.kind]);

  /**
   * Phase 561 — RUN ON ARRIVAL. A step lookup that declares `auto_run: true` runs itself once
   * its step renders with every request arg already resolvable, so a client whose company is
   * already known sees it found without pressing anything.
   *
   * <p>Readiness is {@link lookupReady} — the SAME test the manual control is enabled by — and the
   * run is the SAME {@link runLookup} the control calls, so an auto-run renders, degrades and
   * fills exactly as a press does. `not_found` / `unavailable` / `rate_limited` land as the
   * existing status line with the control left live: an auto-run can never block the form.</p>
   *
   * <p>It depends on `values` so it observes what the carry-forward effect above wrote on step
   * entry: a `default_from` source arms it in the same visit, one render later.</p>
   *
   * <p>CREDIT DISCIPLINE (phase 541 — a credit is spent only on an explicit action; `auto_run` is
   * the declaration author taking that decision, once). Each lookup auto-runs AT MOST ONCE PER
   * FORM SESSION: its id is recorded in {@link autoRanRef} only when an auto-run actually
   * DISPATCHES (never when it is merely armed), so a not-yet-ready lookup can still arm later in
   * the visit, and a recorded one never fires again on a re-render, a blur or a step re-entry.
   * The subject's Search press spends the same credit (card #2664): the press records the id
   * before it runs, so a search they pressed never auto-runs after it — not in the same window,
   * not on a step re-entry. (Two runs sent at once — an auto-run and a press inside one commit, or
   * two presses — are refused by the in-flight guard in {@link runLookup}, which refuses only
   * concurrent duplicates.) And the moment the subject types during a
   * visit the window closes, so a keystroke that happens to complete an arg never dispatches —
   * re-searching, or searching at all after typing, stays the subject's own press.</p>
   *
   * <p>A lookup with no `auto_run` key is never touched here.</p>
   */
  useEffect(() => {
    if (view.kind !== "form") return;
    if (autoRunVisitRef.current !== currentStepId) {
      autoRunVisitRef.current = currentStepId;
      typedThisVisitRef.current = false;
    }
    if (typedThisVisitRef.current) return;
    for (const lookup of stepLookups) {
      if (lookup.auto_run !== true || autoRanRef.current.has(lookup.id)) continue;
      if (!lookupReady(lookup, values)) continue;
      autoRanRef.current.add(lookup.id);
      void runLookup(lookup);
    }
  }, [currentStepId, view.kind, values]);

  function clearErrorsFor(names: readonly string[]) {
    if (names.length === 0) return;
    setErrors((e) => {
      const next = Object.fromEntries(
        Object.entries(e).filter(([key]) => !names.some((n) => key === n || key.startsWith(`${n}.`))),
      );
      return Object.keys(next).length === Object.keys(e).length ? e : next;
    });
  }

  function handleChange(name: string, value: FieldValue) {
    // The keystroke belongs to the step visit ON SCREEN, so it claims that visit itself. React
    // runs the auto-run effect after paint, and after a slow paint that is a LATER task: a
    // keystroke handled first would otherwise be wiped by the effect "starting" this visit, and
    // the keystroke's own render would then auto-run — a keystroke spending a credit.
    autoRunVisitRef.current = currentStepId;
    typedThisVisitRef.current = true;
    setValues((v) => ({ ...v, [name]: value }));
    clearErrorsFor([name]);
    dropFromAutofill(name);
    const stale = declaredLookups(schema).filter((l) => l.trigger_field === name).map((l) => l.id);
    if (stale.length > 0) {
      setLookupUi((s) => {
        // A candidate list is as stale as a status line once its trigger field changes: the
        // subject has re-typed the thing the search was run on, so the answer to that search is
        // no longer an answer to this question.
        if (!stale.some((id) => s[id]?.status || s[id]?.candidates)) return s;
        const next = { ...s };
        for (const id of stale) {
          if (next[id]) {
            next[id] = { ...next[id]!, status: null, payload: undefined, candidates: undefined, chosen: undefined };
          }
        }
        return next;
      });
    }
  }

  /** KYC-design — validate ONE field when its control loses focus, so a wrong UTR is caught before Continue. */
  function handleBlur(name: string) {
    const stepErrors = validateFields(currentStep?.fields ?? flatFields, values, scope);
    setErrors((e) => {
      const message = stepErrors[name];
      if (message === undefined) return e;
      if (e[name] === message) return e;
      return { ...e, [name]: message };
    });
  }

  function setLookup(id: string, state: LookupUiState) {
    setLookupUi((s) => ({ ...s, [id]: state }));
  }

  function dropFromAutofill(name: string) {
    setAutofilled((s) => {
      if (!Object.values(s).some((names) => names.includes(name))) return s;
      const next: Record<string, string[]> = {};
      for (const [id, names] of Object.entries(s)) {
        const kept = names.filter((n) => n !== name);
        if (kept.length > 0) next[id] = kept;
      }
      return next;
    });
    setAutofilledRows((s) => (s[name] ? Object.fromEntries(Object.entries(s).filter(([k]) => k !== name)) : s));
  }

  /**
   * Run one declared lookup and land its result.
   *
   * <p>Phase 561 — `valuesOverride` is the answer set the run is computed against, defaulting to
   * the render's `values`. It exists for the pick CHAIN: `setValues` is asynchronous, so a lookup
   * started from {@link pickCandidate} in the same event would otherwise resolve its request args
   * against the PRE-pick answers (the identifier it needs is exactly what the pick just wrote) and
   * run the never-wipe merge against them too. ALL THREE consumers of "the current answers" —
   * {@link lookupArgs}, {@link lookupPatch} and {@link lookupGroupProvenance} — read the same
   * snapshot, so the args, the merge and the per-row provenance can never disagree about it.</p>
   */
  async function runLookup(lookup: FormLookup, valuesOverride?: FormValues) {
    const run = backend.lookup;
    if (!run || view.kind !== "form" || lookupUi[lookup.id]?.busy) return;
    // Card #2664 — the synchronous in-flight guard (see `inFlightRef`). The set is captured so a
    // run that outlives a form (re)load clears its id from ITS session's set, never the new one's.
    const inFlight = inFlightRef.current;
    if (inFlight.has(lookup.id)) return;
    inFlight.add(lookup.id);
    try {
      await dispatchLookup(run, lookup, valuesOverride);
    } finally {
      inFlight.delete(lookup.id);
    }
  }

  async function dispatchLookup(
    run: NonNullable<typeof backend.lookup>,
    lookup: FormLookup,
    valuesOverride?: FormValues,
  ) {
    const current = valuesOverride ?? values;
    setLookup(lookup.id, { busy: true, status: null });
    setAutofilled((s) => {
      if (s[lookup.id] === undefined) return s;
      const next = { ...s };
      delete next[lookup.id];
      return next;
    });
    let result: LookupResult;
    try {
      result = await run(lookup.id, lookupArgs(lookup, current));
    } catch {
      setLookup(lookup.id, { busy: false, status: { tone: "notice", message: LOOKUP_MESSAGE_UNAVAILABLE } });
      return;
    }
    if (result.ok) {
      // A `choose` lookup fills NOTHING on its own: it found a shortlist, and the question is now
      // the subject's to answer. No status line either — the list below the field says everything
      // there is to say, including when it came back empty.
      if (lookup.choose) {
        setLookup(lookup.id, { busy: false, status: null, payload: result, candidates: chooseCandidates(lookup, result) });
        return;
      }
      // `current` is what makes the never-wipe contract live: the patch is merged against the
      // answers the subject has already given, so a second run keeps every row they added and
      // every cell they typed.
      const patch = lookupPatch(schema, lookup, result, current);
      if (Object.keys(patch).length > 0) {
        setValues((v) => ({ ...v, ...patch }));
        clearErrorsFor(Object.keys(patch));
        setAutofilled((s) => ({ ...s, [lookup.id]: Object.keys(patch) }));
        // Per-ROW provenance, from the same merge and the same `current`: the chip marks what the
        // upstream contributed and never a row the subject typed. The page does not read the
        // `populate_group` shape at all — which group, and which of its rows, is one answer.
        const provenance = lookupGroupProvenance(schema, lookup, result, current);
        if (provenance) {
          setAutofilledRows((s) => ({ ...s, [provenance.target]: [...provenance.rows] }));
        }
        setLookup(lookup.id, { busy: false, status: { tone: "success", message: lookupMessage(result) }, payload: result });
        return;
      }
      setLookup(lookup.id, { busy: false, status: { tone: "notice", message: LOOKUP_MESSAGE_UNAVAILABLE } });
      return;
    }
    setLookup(lookup.id, { busy: false, status: { tone: "notice", message: lookupMessage(result) } });
  }

  /**
   * Phase 524 — the PICK. It writes the declaration's `choose.populate` targets through the SAME
   * setter, the same error-clearing and the same autofill provenance an ordinary lookup autofill
   * uses, so a picked answer and a filled answer are the same kind of thing to everything
   * downstream — the notice on the step that holds them, the "From the register" chip, the review
   * page and the payload.
   *
   * What it deliberately does NOT do: submit, advance a step, lock a field, or clear an answer the
   * subject typed. `choosePatch` writes only the declared targets it could resolve, so a pick adds
   * and never wipes; and the list collapses to the row that was chosen rather than disappearing,
   * so the subject can see what they picked.
   *
   * Phase 561 — THE CHAIN. When the declaration names `choose.then_run`, the pick then runs that
   * lookup through the ordinary {@link runLookup} path, handed `{ ...values, ...patch }` — the
   * SAME patch just given to `setValues` — so its request args resolve against the identifier
   * the pick wrote on the first attempt, and its `populate` / `populate_group` land through the
   * existing never-wipe merge. The chain is still not a commitment: it fills answers and nothing
   * else, and a degraded chained run leaves the pick standing. Tapping the already-confirmed
   * candidate again does not spend a second call while its record is on screen; after a failed
   * chained run, that same tap is the retry.
   */
  function pickCandidate(lookup: FormLookup, index: number) {
    const ui = lookupUi[lookup.id];
    const candidate = ui?.candidates?.[index];
    if (candidate === undefined) return;
    const patch = choosePatch(schema, lookup, candidate.raw);
    if (Object.keys(patch).length > 0) {
      setValues((v) => ({ ...v, ...patch }));
      clearErrorsFor(Object.keys(patch));
      setAutofilled((s) => ({ ...s, [lookup.id]: Object.keys(patch) }));
    }
    setLookupUi((s) => ({ ...s, [lookup.id]: { ...(s[lookup.id] ?? IDLE_LOOKUP), chosen: index } }));

    const target = chainTarget(lookup);
    if (target === undefined) return;
    const targetUi = lookupUi[target.id];
    if (ui?.chosen === index && (targetUi?.busy || targetUi?.status?.tone === "success")) return;
    const picked = { ...values, ...patch };
    if (lookupReady(target, picked)) {
      void runLookup(target, picked);
    } else {
      // Nothing to run it on: forget whatever an earlier chain found, so a record for a
      // DIFFERENT candidate can never render inside this one's card.
      setLookup(target.id, IDLE_LOOKUP);
    }
  }

  /**
   * Phase 561 — the lookup a `choose` lookup's pick chains into (`choose.then_run`), resolved on
   * the SAME step. Undefined when the declaration names none (Phase-524 behaviour) or names an id
   * the step does not carry (ARD-DSL-038 rejects that at deploy; skipped, not trusted, here).
   */
  function chainTarget(lookup: FormLookup): FormLookup | undefined {
    const id = lookup.choose?.then_run;
    if (id === undefined) return undefined;
    return stepLookups.find((candidate) => candidate.id === id && candidate.id !== lookup.id);
  }

  /**
   * Phase 561 — the ids of every lookup on this step that some `choose` lookup's pick RUNS
   * (`choose.then_run`). The chain owns them: they get no control of their own and no standalone
   * result card, which is what leaves ONE search box and ONE card on the step. Derived from the
   * declaration and nothing else — no id, integration or field name is special here.
   */
  const chainedIds = new Set(
    stepLookups.flatMap((lookup) => (lookup.choose?.then_run !== undefined ? [lookup.choose.then_run] : [])),
  );

  /**
   * Card #2717 — the trigger fields of this step's LIST-ONLY lookups: a `choose` lookup declaring
   * `list_only: true` that no pick chains into. Each renders as its list's heading with no input
   * box (FormRenderer), so nothing typed can clear the list. Derived from the declaration alone.
   */
  const listOnlyTriggers = new Set(
    stepLookups
      .filter((lookup) => lookup.list_only === true && lookup.choose !== undefined && !chainedIds.has(lookup.id))
      .map((lookup) => lookup.trigger_field),
  );

  /**
   * A lookup's declared `show` rows, projected from one result envelope — the found-record card's
   * rows, and (Phase 561) the chained record's rows inside the confirmed candidate. One projection,
   * so the two presentations of a record can never read it differently.
   */
  function showRows(lookup: FormLookup, payload: unknown) {
    return (lookup.show?.rows ?? [])
      .map(({ label, path }) => {
        const raw = readPath(payload, path);
        const value = envelopeDisplayValue(raw);
        return { label, value, mono: /number|reference|code/i.test(label), status: /status/i.test(path) };
      })
      .filter((row) => row.value.length > 0);
  }

  function fieldAction(field: FieldSpec) {
    const triggered = stepLookups.filter((lookup) => lookup.trigger_field === field.name && !chainedIds.has(lookup.id));
    if (triggered.length === 0) return null;
    return triggered.map((lookup) => {
      const ui = lookupUi[lookup.id] ?? IDLE_LOOKUP;
      return (
        <LookupAction
          key={lookup.id}
          lookup={lookup}
          ready={lookupReady(lookup, values)}
          busy={ui.busy}
          status={ui.status}
          onRun={() => {
            // Card #2664 — a press IS this visit's run: recorded before it dispatches, so the
            // auto-run effect (which runs after paint, possibly in this same window) never sends
            // the same search a second time. Recording a lookup that has no `auto_run` is inert.
            autoRanRef.current.add(lookup.id);
            void runLookup(lookup);
          }}
          announceSuccess={!lookup.show && !lookup.choose}
        />
      );
    });
  }

  /**
   * Everything that renders UNDER one field's row. Two independent contributors share the slot:
   * the Phase-532 ctx pre-answer panel and the lookup's own result card / candidate list. The
   * pre-answer goes first — it is about the question itself, while a lookup card is about
   * something the assessor just did.
   */
  function fieldSlot(field: FieldSpec) {
    const preAnswerPanel = fromCtxSlot(field);
    const lookups = lookupSlot(field);
    if (preAnswerPanel === null && lookups === null) return null;
    return <>{preAnswerPanel}{lookups}</>;
  }

  /**
   * Phase 532 — the evidence rows, the "answer it yourself" prompt and the change-reason box for
   * a field the SERVER had something to say about. Everything it renders was resolved server-side;
   * this reads the served keys and the answer set, and nothing else.
   *
   * <p>TWO declarations serve those keys and this slot renders BOTH, because they are one thing to
   * a reader: a `from_ctx` block on a question of the form (ARD-DSL-051), and a bound approval
   * gate's `computed_from` on the decision step the server generates from it (ARD-DSL-053). Same
   * pre-answer channel, same source chip, same "the file could not answer" prompt.</p>
   *
   * <p>The CHANGE-REASON box is the one part that is not shared, and the split is declared rather
   * than guessed ({@link ownsChangeReason}): only the `from_ctx` convention writes its reason to
   * `<name>_change_reason`. The gate declares a reason FIELD of its own, served with the reveal
   * predicate that shows it the instant the approver moves off the computed value — so the
   * interaction is the same one, rendered by the same machinery, and never twice on one row.</p>
   */
  function fromCtxSlot(field: FieldSpec) {
    if (isGroupField(field)) return null;
    const spec = field as ScalarFieldSpec;
    const served = preAnswer(spec);
    const reasonName = changeReasonName(spec.name);
    return (
      <FromCtxPanel
        key={`${spec.name}-from-ctx`}
        spec={spec}
        preAnswerLabel={served === undefined ? null : optionLabelOf(spec, served)}
        changed={ownsChangeReason(spec) && changedFromPreAnswer(spec, values)}
        reason={asString(values[reasonName])}
        error={errors[reasonName]}
        onReasonChange={(value) => handleChange(reasonName, value)}
        onRevert={() => revertToPreAnswer(spec)}
      />
    );
  }

  /** A choice answer as the copy the assessor reads — the schema's own options list, once. */
  function optionLabelOf(spec: ScalarFieldSpec, value: string): string {
    return optionEntries(spec).find((entry) => entry.value === value)?.label ?? value;
  }

  /**
   * Put the file's answer back, and drop the obligation with it: the reason box exists only
   * while the answer differs, so clearing the reason here is what stops a sentence written
   * about a change that no longer exists from reaching the payload.
   */
  function revertToPreAnswer(spec: ScalarFieldSpec) {
    const served = preAnswer(spec);
    if (served === undefined) return;
    const reasonName = changeReasonName(spec.name);
    setValues((v) => ({ ...v, [spec.name]: served, [reasonName]: "" }));
    clearErrorsFor([spec.name, reasonName]);
    dropFromAutofill(spec.name);
  }

  /**
   * What renders UNDER the trigger field once its lookup has run: the found-record card for a
   * `show` lookup, and the candidate list for a `choose` one. They are mutually exclusive in the
   * grammar, so one lookup contributes at most one of them.
   *
   * <p>Phase 561 — a lookup a pick CHAINS into (`choose.then_run`) contributes no card of its own
   * here: its record renders INSIDE the confirmed candidate of the list that ran it, so the step
   * shows one card, not a candidate stacked over a found record.</p>
   */
  function lookupSlot(field: FieldSpec) {
    const triggered = stepLookups.filter(
      (lookup) => lookup.trigger_field === field.name && (lookup.show || lookup.choose) && !chainedIds.has(lookup.id),
    );
    if (triggered.length === 0) return null;
    return triggered.map((lookup) => {
      if (lookup.choose) {
        const ui = lookupUi[lookup.id];
        // No run yet ⇒ nothing to say. An EMPTY list is a result and does get said.
        if (!ui || ui.candidates === undefined) return null;
        const target = chainTarget(lookup);
        const targetUi = target ? lookupUi[target.id] : undefined;
        const confirmedRows =
          target !== undefined && targetUi?.status?.tone === "success" && targetUi.payload !== undefined
            ? showRows(target, targetUi.payload)
            : undefined;
        return (
          <LookupChoiceList
            key={lookup.id}
            id={lookup.id}
            eyebrow={lookup.choose.eyebrow}
            candidates={ui.candidates}
            chosen={ui.chosen}
            onPick={(index) => pickCandidate(lookup, index)}
            confirmedRows={confirmedRows}
            confirmedBusy={targetUi?.busy === true}
            confirmedNotice={targetUi?.status?.tone === "notice" ? targetUi.status.message : undefined}
          />
        );
      }
      const ui = lookupUi[lookup.id];
      if (!ui || ui.status?.tone !== "success" || ui.payload === undefined || !lookup.show) return null;
      const title = String(readPath(ui.payload, lookup.show.title) ?? "").trim();
      const rows = showRows(lookup, ui.payload);
      return <LookupResultCard key={lookup.id} eyebrow={lookup.show.eyebrow ?? "We found this record"} title={title || "Record found"} rows={rows} />;
    });
  }

  function stepAutofillNotices() {
    const fields = visibleFields(currentStep?.fields, scope);
    if (fields.length === 0) return null;
    return declaredLookups(schema).map((lookup) => {
      const filled = autofilled[lookup.id];
      if (filled === undefined) return null;
      // Card #2878 — a hidden field is never named to the person, not even as "we filled this in".
      const labels = fields
        .filter((field) => filled.includes(field.name) && !(field as ScalarFieldSpec).hidden)
        .map(fieldLabel);
      if (labels.length === 0) return null;
      return <AutofillNotice key={lookup.id} notice={lookup.notice} fieldLabels={labels} />;
    });
  }

  /**
   * name → source chip. ONE provenance display, two sources that write to it:
   *
   *  - Phase 532, a `from_ctx` pre-answer the SERVER resolved — its declared `source_label`,
   *    shown only while the answer on the row is still the one the file gave. The chip is a
   *    claim about the CURRENT answer, so it goes the moment the assessor changes it and the
   *    reason box (which restates what the file said) takes over;
   *  - a lookup autofill, written last so the more recent action names itself if the two ever
   *    land on the same field.
   *
   * Neither reads the other's state: the pre-answer chip is derived from the served schema and
   * the answer set, the lookup chip from `autofilled`, and `dropFromAutofill` touches only the
   * latter.
   */
  function prefilledMap(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const field of declaredFields(schema)) {
      if (preAnswer(field) === undefined || changedFromPreAnswer(field, values)) continue;
      const label = (field as ScalarFieldSpec).from_ctx_source_label?.trim();
      if (label) out[field.name] = label;
    }
    for (const lookup of declaredLookups(schema)) {
      const names = autofilled[lookup.id];
      if (!names) continue;
      for (const name of names) out[name] = lookup.source_label ?? "Prefilled";
    }
    return out;
  }

  function prefilledRowsMap(): Record<string, ReadonlySet<number>> {
    return Object.fromEntries(Object.entries(autofilledRows).map(([k, v]) => [k, new Set(v)]));
  }

  /** Review page: group rows as cards, keyed by the step's index in the visible list. */
  function reviewCards(): Record<number, ReviewCard[]> {
    const out: Record<number, ReviewCard[]> = {};
    steps.forEach((step, index) => {
      if (step.review) return;
      for (const field of visibleFields(step.fields, scope)) {
        if (!isGroupField(field)) continue;
        const group = field as GroupFieldSpec;
        const rows = asRows(values[group.name]);
        if (rows.length === 0) continue;
        const cards = rows.map((row, i) => {
          const summary = rowSummary(group, row);
          const lines = summary
            ? [summary]
            : // Per ROW: a card shows the subfields THIS row shows, judged against this row's
              // own answers — never the form scope, which has no row to answer `row.` with.
              visibleFields(group.fields, rowScope(scope, row))
                .map((sub) => {
                  // Through the shared cell display rule, so an `address` subfield's nested
                  // cell reads as its one-line address rather than as "[object Object]" —
                  // a string cell prints exactly as it did before (Phase 541).
                  // A declared read_only subfield reads back as the form shows it (its option
                  // label, its described codes), never a raw token (L-97 rule d; ADR-106).
                  const cell = sub.read_only === true && !isGroupField(sub)
                    ? readOnlyText(readOnlyShown(sub as ScalarFieldSpec, row as FormValues))
                    : cellLine(row[sub.name]);
                  return cell.trim() ? `${fieldLabel(sub)}: ${cell}` : "";
                })
                .filter((l) => l.length > 0);
          return { title: rowTitle(group, row, i, fieldLabel(group)), lines };
        });
        (out[index] ??= []).push(...cards);
      }
    });
    return out;
  }

  function readOnlyText(shown: ReturnType<typeof readOnlyShown>): string {
    if (shown === null) return "";
    if ("lines" in shown) return shown.lines.join(", ");
    if ("address" in shown) return formatAddress(shown.address);
    return shown.value;
  }

  function monoFields(): Set<string> {
    const names = new Set<string>();
    for (const step of steps) for (const f of step.fields ?? []) if (!isGroupField(f) && f.format) names.add(f.name);
    return names;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (view.kind !== "form" || submitting) return;
    if (wizard && !isLastStep) {
      const stepErrors = validateFields(currentStep?.fields, values, scope);
      if (Object.keys(stepErrors).length > 0) { setErrors(stepErrors); return; }
      setErrors({});
      setStepIndex(currentIndex + 1);
      return;
    }
    const errs = validateForm(schema, values, scope);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      const target = wizard ? firstStepWithError(schema, errs, scope) : -1;
      if (target >= 0) setStepIndex(target);
      return;
    }
    setSubmitFailure(null);
    setSubmitting(true);
    try {
      const outcome = await backend.submit(buildPayload(schema, values, scope));
      if (outcome.kind === "signing-in") return;
      // VERBATIM: the engine's sentence is shown exactly as it arrived, or the generic copy when
      // it sent none. The page neither composes a refusal nor reads a code.
      if (outcome.kind === "failed") { setSubmitFailure(outcome.message ?? SUBMIT_FAILED_MESSAGE); return; }
      autosaveRef.current?.cancel();
      // Phase 572 wave 3 task 3.1 — follow-on navigation: if the operator holds the gate's
      // required_role, navigate to the approval form for the same job. The link is NOT invented
      // client-side — it comes from the engine's submit response.
      if (outcome.kind === "follow-on") {
        window.location.href = outcome.follow_on.link;
        return;
      }
      setView({ kind: "submitted", response: view.response });
      setDocumentTitle(`Form submitted — ${view.response.tenant_slug}`);
    } catch {
      setSubmitFailure(SUBMIT_FAILED_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  }

  function saveForLater() {
    void autosaveRef.current?.flush(values);
    setSavedLater(true);
  }

  /**
   * Phase 572 wave 3 task 3.3 — handles settling a blocker. On success, re-reads /context so the
   * settled item disappears and the decision becomes available in place.
   */
  async function handleSettleBlocker(
    item: OpenHoldItem,
    decision: "received" | "not_required",
    fields: { note?: string; received_date?: string; reason?: string },
  ): Promise<{ ok: true } | { ok: false; message: string }> {
    if (!backend.settleBlocker) {
      return { ok: false, message: "This form surface does not support settling blockers." };
    }
    const result = await backend.settleBlocker(item, decision, fields);
    if (result.kind === "failed") {
      return { ok: false, message: result.message };
    }
    // On success, re-read /context to update the blockers list and the form state.
    const reloadOutcome = await backend.load();
    if (reloadOutcome.kind === "ready") {
      const response = reloadOutcome.response;
      setBlockers(response.blockers ?? []);
      // The schema may have changed (decision step may now be available), so update the view.
      // Values and step index stay the same — the approver keeps their place.
      setView({ kind: "form", response });
    }
    return { ok: true };
  }

  const tenantName = view.kind === "form" || view.kind === "submitted" || view.kind === "waiting" || view.kind === "gave-up"
    ? (view.response.theme.display_name ?? view.response.tenant_slug)
    : (theme?.display_name ?? "");
  const logoUrl = theme?.logo_url ?? "/default-logo.svg";
  const showRail = view.kind === "form" && wizard && !isWelcome && sections.length > 0;
  const footerNote = theme?.footer_note;

  const headerAside = view.kind === "form" ? (
    <>
      <SaveStatusLine status={saveStatus} />
      <SaveLater onSave={saveForLater} disabled={submitting} />
    </>
  ) : null;

  const reviewSummary = view.kind === "form" && currentStep?.review ? answerSummary(schema, values, scope) : [];
  const review = currentStep?.review ? reviewSections(reviewSummary, reviewCards()) : { summary: [], cards: {} };

  /**
   * The review page reads by SECTION, not by step: consecutive steps sharing a `section`
   * merge into one card (Change goes to the section's first step). Group answers show only
   * as people/row cards, never doubled as flat terms; an optional question left blank is
   * left out rather than listed as "Not answered"; a consent reads as a tick plus its sentence.
   */
  function reviewSections(
    summary: ReadonlyArray<SummaryStep>,
    cardsByStep: Readonly<Record<number, ReviewCard[]>>,
  ): { summary: SummaryStep[]; cards: Record<number, ReviewCard[]> } {
    const merged: SummaryStep[] = [];
    const cards: Record<number, ReviewCard[]> = {};
    let lastKey: string | undefined;
    for (const entry of summary) {
      const step = steps[entry.index];
      const stepFields = visibleFields(step?.fields, scope);
      const groups = stepFields.filter(isGroupField);
      const rows = entry.rows
        .filter((row) => !groups.some((g) => row.name === g.name || row.name.startsWith(`${g.name}.`)))
        .filter((row) => {
          if (row.display !== NOT_ANSWERED) return true;
          const field = stepFields.find((f) => f.name === row.name);
          return field?.required === true;
        })
        .map((row) => {
          const field = stepFields.find((f) => f.name === row.name);
          return field && !isGroupField(field) && field.type === "boolean" && row.display === ANSWER_YES
            ? { ...row, check: true }
            : row;
        });
      const emptyGroups = groups
        .filter((g) => asRows(values[g.name]).length === 0)
        .map((g) => ({ name: g.name, label: fieldLabel(g), display: NOT_ANSWERED }));
      const key = step?.section ?? `step:${entry.index}`;
      const stepCards = cardsByStep[entry.index] ?? [];
      const last = merged[merged.length - 1];
      if (last !== undefined && key === lastKey) {
        last.rows.push(...rows, ...emptyGroups);
        (cards[last.index] ??= []).push(...stepCards);
      } else {
        merged.push({ index: entry.index, title: step?.section ?? entry.title, rows: [...rows, ...emptyGroups] });
        if (stepCards.length > 0) cards[entry.index] = [...stepCards];
        lastKey = key;
      }
    }
    return { summary: merged.filter((m) => m.rows.length > 0 || (cards[m.index] ?? []).length > 0), cards };
  }

  return (
    <div className="form-page">
      {view.kind !== "loading" && <TenantHeader logoUrl={logoUrl} tenantName={tenantName} aside={headerAside} />}
      <div className={`form-body${showRail ? "" : " form-body-no-rail"}`}>
        {showRail && (
          <div className="form-rail-slot">
            <StepProgress sections={sections} current={Math.max(0, currentSection)} />
          </div>
        )}
        <div className="form-main">
          {view.kind === "loading" && <FormSkeleton />}
          {view.kind === "form" && (
            <form onSubmit={handleSubmit} noValidate>
              {wizard ? (
                <>
                  {/* The form's own title is the page's h1 (kept small as an eyebrow); the step title
                      is the big heading the client reads, as an h2 — the same hierarchy on every step. */}
                  <h1 ref={formHeadingRef} className="form-eyebrow" tabIndex={-1}>{schema.title ?? "Complete your form"}</h1>
                  {currentStep?.title && (
                    <h2 className={`step-heading${isWelcome ? " step-heading-welcome" : ""}`}>{currentStep.title}</h2>
                  )}
                  {isWelcome && schema.description && <p className="form-instruction">{schema.description}</p>}
                  {currentStep?.content && <Copy text={currentStep.content} className="step-content" />}
                  {savedLater && <p className="save-status" role="status">Saved. Come back to this link any time to carry on.</p>}
                  {submitFailure !== null && (
                    <div className="submission-error" role="alert">{submitFailure}</div>
                  )}
                  {/* Phase 572 wave 3 task 3.3 — the approval page's blockers, rendered when present
                      and the backend supports settling them. The blockers must be settled before the
                      approver can submit. Each blocker shows its declared label and message, and the
                      actions the server said apply. Settling re-reads /context so the item disappears
                      and the decision becomes available in place. */}
                  {blockers.length > 0 && backend.settleBlocker !== undefined && (
                    <BlockerPanel blockers={blockers} onSettle={handleSettleBlocker} />
                  )}
                  {/* Phase 532 — a card may declare `visible_when`, so it is filtered through the
                      SAME bounded predicate evaluator a field is, before InfoCards sees it. A
                      card with no predicate is unconditionally visible, exactly as before.

                      It may also declare read-only `rows`, resolved against the SAME served ctx
                      snapshot the predicates read — which is why the snapshot goes in here rather
                      than a second "and also send these values" channel. The card names the paths;
                      the server decided which of them it was willing to answer. */}
                  {currentStep?.cards && (
                    <InfoCards cards={visibleFields(currentStep.cards, scope)} ctx={ctxSnapshot} />
                  )}
                  {currentStep?.review ? (
                    <ReviewStep
                      summary={review.summary}
                      onChangeStep={setStepIndex}
                      cards={review.cards}
                      monoFields={monoFields()}
                      nextSteps={currentStep.next_steps}
                      declaration={currentStep.declaration}
                    />
                  ) : (currentStep?.fields ?? []).length > 0 ? (
                    <div className="form-card">
                      {stepAutofillNotices()}
                      <FormRenderer
                        fields={currentStep?.fields}
                        values={values}
                        errors={errors}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        scope={scope}
                        fieldAction={stepLookups.length > 0 ? fieldAction : undefined}
                        fieldSlot={fieldSlot}
                        listOnlyFields={listOnlyTriggers}
                        prefilled={prefilledMap()}
                        prefilledRows={prefilledRowsMap()}
                        onAddressLookup={onAddressLookup}
                      />
                    </div>
                  ) : null}
                  <StepNav
                    canGoBack={currentIndex > 0}
                    onBack={() => { setErrors({}); setStepIndex(currentIndex - 1); }}
                    isLast={isLastStep}
                    isFirst={isWelcome}
                    submitting={submitting}
                    submitLabel={submitLabel}
                  />
                </>
              ) : (
                <>
                  <h1 ref={formHeadingRef} className="form-heading">
                    {schema.title ?? "Tell us about yourself"}
                  </h1>
                  <p className="form-instruction">
                    {schema.description ?? "Please fill in the details below."}
                  </p>
                  {submitFailure !== null && (
                    <div className="submission-error" role="alert">{submitFailure}</div>
                  )}
                  <div className="form-card">
                    <FormRenderer
                      fields={flatFields}
                      values={values}
                      errors={errors}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      scope={scope}
                      // A pre-answer is a property of a FIELD, not of a wizard: the flat
                      // single-page contract gets the same chip and the same panel.
                      fieldSlot={fieldSlot}
                      prefilled={prefilledMap()}
                      onAddressLookup={onAddressLookup}
                    />
                    {isEmptySchema && (
                      <p className="form-empty" role="status">
                        There is nothing to fill in here. Submit to confirm you have read this.
                      </p>
                    )}
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="button-primary" disabled={submitting} aria-disabled={submitting}>
                      {submitLabel}
                    </button>
                  </div>
                </>
              )}
            </form>
          )}
          {view.kind === "submitted" && (
            <StatusCard
              variant="success"
              heading={view.response.form_schema?.submitted?.title}
              body={view.response.form_schema?.submitted?.body}
              nextSteps={reviewStep?.next_steps}
              action={
                followOnBlock && (
                  <FollowOnAction block={followOnBlock} settled={followOnTransport ? followOnSettled : FOLLOW_ON_NO_TRANSPORT} />
                )
              }
            />
          )}
          {/* Phase 572 wave 3 task 3.1 — the gate-not-open waiting state: show the server's
              plain-English message while polling. */}
          {view.kind === "waiting" && (
            <div className="status-card waiting-card">
              <h1>Waiting for this file</h1>
              <p>{view.message}</p>
              <p className="waiting-status" role="status">Checking...</p>
            </div>
          )}
          {/* Phase 572 wave 3 task 3.1 — the give-up state: the gate never opened. Offer a retry
              rather than leaving the approver stuck. */}
          {view.kind === "gave-up" && (
            <div className="status-card gave-up-card">
              <h1>This approval is not available</h1>
              <p>{GATE_GAVE_UP_MESSAGE}</p>
              <button
                type="button"
                className="button-primary"
                onClick={() => window.location.reload()}
              >
                Try again
              </button>
            </div>
          )}
          {view.kind === "error" && <StatusCard variant={view.variant} />}
        </div>
      </div>
      {view.kind !== "loading" && <PageFooter note={footerNote} />}
    </div>
  );
}
